import { useCallback, useState } from 'react';
import { Controller, useFieldArray, UseFormReturn } from 'react-hook-form';
import { Box, Button, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { TProjectFormData } from 'src/@types';
import { RHFDateWithCheckbox, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import ItemActionMenu from 'src/components/ItemActionMenu';
import { numberToSimpleKoreanCurrency } from 'src/utils/koreanCurrency';

import BulkRegisterDialog from './BulkRegisterDialog';

export interface AssignDepositsSectionProps {
  methods: UseFormReturn<TProjectFormData>;
  readonly?: boolean;
}

/**
 * 과제 입금내역 섹션
 * - depositAmt: 입금액
 * - depositDay / depositYn: 입금 예정일 + 입금 여부
 * - memo: 비고
 */
function AssignDepositsSection({ methods, readonly }: AssignDepositsSectionProps) {
  const { enqueueSnackbar } = useSnackbar();

  const {
    fields: assignDeposits,
    append: appendAssignDeposit,
    insert: insertAssignDeposit,
    remove: removeAssignDeposit,
  } = useFieldArray({
    control: methods.control,
    name: 'assignDeposits',
  });

  const handleAddItem = useCallback(() => {
    appendAssignDeposit({
      assignDepositSid: null,
      depositAmt: 0,
      depositDay: Date.now(),
      depositYn: false,
      memo: '',
    });
  }, [appendAssignDeposit]);

  const handleCopyItem = useCallback(
    (index: number) => {
      const current = methods.getValues('assignDeposits');
      const item = current?.[index];
      if (!item) return;

      insertAssignDeposit(index + 1, {
        assignDepositSid: null,
        depositAmt: item.depositAmt || 0,
        depositDay: item.depositDay ?? Date.now(),
        depositYn: false,
        memo: item.memo || '',
      });
      enqueueSnackbar('입금내역이 복사되었습니다.', { variant: 'success' });
    },
    [insertAssignDeposit, methods, enqueueSnackbar],
  );

  // 일괄 등록 다이얼로그
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkAmt, setBulkAmt] = useState<number>(0);
  const [bulkMemo, setBulkMemo] = useState<string>('');

  const handleBulkConfirm = useCallback(
    (rows: { depositDay: number }[]) => {
      rows.forEach((row) => {
        appendAssignDeposit({
          assignDepositSid: null,
          depositAmt: bulkAmt,
          depositDay: row.depositDay,
          depositYn: false,
          memo: bulkMemo,
        });
      });
      enqueueSnackbar(`${rows.length}건의 입금내역이 추가되었습니다.`, { variant: 'success' });
      setBulkAmt(0);
      setBulkMemo('');
    },
    [appendAssignDeposit, bulkAmt, bulkMemo, enqueueSnackbar],
  );

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {!readonly && (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="outlined"
            size="small"
            startIcon={<Iconify icon="mdi:calendar-multiselect" />}
            onClick={() => setBulkOpen(true)}
          >
            일괄 등록
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Iconify icon="mdi:plus" />}
            onClick={handleAddItem}
          >
            입금내역 추가
          </Button>
        </Stack>
      )}

      <BulkRegisterDialog
        open={bulkOpen}
        title="입금내역 일괄 등록"
        dateFields={[{ key: 'depositDay', label: '입금 예정일', defaultDay: 'LAST' }]}
        onClose={() => setBulkOpen(false)}
        onConfirm={(rows) => handleBulkConfirm(rows.map((r) => ({ depositDay: r.depositDay })))}
      >
        <Stack spacing={2}>
          <TextField
            label="입금액"
            size="small"
            value={bulkAmt ? bulkAmt.toLocaleString() : ''}
            onChange={(e) => {
              const v = Number(e.target.value.replace(/,/g, ''));
              if (!isNaN(v)) setBulkAmt(v);
            }}
            InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
            helperText={bulkAmt ? numberToSimpleKoreanCurrency(bulkAmt) : ''}
          />
          <TextField
            label="비고"
            size="small"
            multiline
            minRows={2}
            value={bulkMemo}
            onChange={(e) => setBulkMemo(e.target.value)}
          />
        </Stack>
      </BulkRegisterDialog>

      {assignDeposits.length === 0 ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            p: 3,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
            color: 'text.secondary',
          }}
        >
          <Typography variant="body2">입금내역이 없습니다.</Typography>
        </Box>
      ) : (
        assignDeposits.map((field, index) => (
          <Box
            key={field.id}
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{
              p: 2,
              pl: readonly ? 2 : 4,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              position: 'relative',
            }}
          >
            {!readonly && (
              <ItemActionMenu
                onDelete={() => removeAssignDeposit(index)}
                onCopy={() => handleCopyItem(index)}
                deleteLabel="삭제"
                copyLabel="복사"
              />
            )}

            {/* 금액 + 비고 */}
            <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2} alignItems="flex-start">
              <Controller
                name={`assignDeposits.${index}.depositAmt`}
                control={methods.control}
                render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="입금액"
                    value={field.value ? field.value.toLocaleString() : ''}
                    placeholder="입금액"
                    error={!!error}
                    onChange={(e) => {
                      const numericValue = Number(e.target.value.replace(/,/g, ''));
                      if (!isNaN(numericValue)) {
                        field.onChange(numericValue);
                      }
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">원</InputAdornment>,
                      readOnly: readonly,
                    }}
                    helperText={
                      error
                        ? error?.message
                        : field.value
                        ? `${numberToSimpleKoreanCurrency(field.value)}`
                        : ''
                    }
                    inputRef={ref}
                    sx={{ flex: 1, minWidth: 180 }}
                  />
                )}
              />
              <RHFTextField
                name={`assignDeposits.${index}.memo`}
                label="비고"
                multiline
                inputProps={{ readOnly: readonly }}
                sx={{ flex: 2, minWidth: 200 }}
              />
            </Box>

            {/* 입금 예정일 + 입금 여부 */}
            <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2}>
              <RHFDateWithCheckbox
                dateName={`assignDeposits.${index}.depositDay`}
                checkboxName={`assignDeposits.${index}.depositYn`}
                label="입금 예정일"
                checkedLabel="입금일"
                checkboxLabel="입금 여부"
                readOnly={readonly}
                sx={{ flex: 1, minWidth: 250 }}
              />
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
}

export default AssignDepositsSection;
